"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import type { CityPriceMapItem, MarketSummary } from "@/lib/api";
import { List, Map } from "lucide-react";

const HalPriceMap = dynamic(
  () => import("@/components/sections/HalPriceMap"),
  {
    ssr: false,
    loading: () => (
      <div className="flex aspect-[2.3/1] min-h-[320px] items-center justify-center border border-(--color-border) bg-(--color-surface) text-[13px] text-(--color-muted)">
        Harita yükleniyor…
      </div>
    ),
  },
);

interface Props {
  markets: MarketSummary[];
  halCityPrices?: CityPriceMapItem[];
  borsaCityPrices?: CityPriceMapItem[];
  mode?: "all" | "borsa";
  children: React.ReactNode;
}

export default function HalViewToggle({ markets, halCityPrices, borsaCityPrices, mode = "all", children }: Props) {
  const [view, setView] = useState<"list" | "map">("map");

  return (
    <section className="mt-10" aria-labelledby="market-source-title">
      <div className="mb-5">
        <h2 id="market-source-title" className="font-(family-name:--font-display) text-[clamp(1.75rem,3vw,2.4rem)] font-black tracking-[-0.035em] text-(--color-foreground)">
          {mode === "borsa" ? "Türkiye’deki ticaret borsaları" : "Türkiye'deki haller ve borsalar"}
        </h2>
        <p className="mt-2 max-w-3xl text-[14px] leading-6 text-(--color-muted) sm:text-[15px]">
          {mode === "borsa" ? "İli seçin; ticaret borsasını ve güncel veri kapsamını inceleyin." : "İli seçin; hal, borsa ve güncel veri kapsamını birlikte inceleyin."}
        </p>
      </div>

      <div className="border border-(--color-border) bg-(--color-surface)">
        <div className="flex border-b border-(--color-border)" role="tablist" aria-label={mode === "borsa" ? "Borsa görünümü" : "Hal ve borsa görünümü"}>
          <button
            type="button"
            role="tab"
            aria-selected={view === "map"}
            aria-controls="hal-map-panel"
            id="hal-map-tab"
            onClick={() => setView("map")}
            className={
              "relative flex min-h-14 flex-1 items-center justify-center gap-2 px-5 text-[14px] font-bold transition-colors sm:flex-none " +
              (view === "map"
                ? "text-(--color-foreground) after:absolute after:inset-x-0 after:bottom-[-1px] after:h-[3px] after:bg-(--color-accent)"
                : "text-(--color-muted) hover:text-(--color-foreground)")
            }
          >
            <Map className="size-4" aria-hidden />
            Harita
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "list"}
            aria-controls="hal-list-panel"
            id="hal-list-tab"
            onClick={() => setView("list")}
            className={
              "relative flex min-h-14 flex-1 items-center justify-center gap-2 px-5 text-[14px] font-bold transition-colors sm:flex-none " +
              (view === "list"
                ? "text-(--color-foreground) after:absolute after:inset-x-0 after:bottom-[-1px] after:h-[3px] after:bg-(--color-accent)"
                : "text-(--color-muted) hover:text-(--color-foreground)")
            }
          >
            <List className="size-4" aria-hidden />
            Liste <span className="font-(family-name:--font-mono) text-[11px] font-medium text-(--color-muted)">· {markets.length} kaynak</span>
          </button>
        </div>

        <div
          id="hal-map-panel"
          role="tabpanel"
          aria-labelledby="hal-map-tab"
          hidden={view !== "map"}
          className="p-3 sm:p-4"
        >
          <HalPriceMap markets={markets} halCityPrices={halCityPrices} borsaCityPrices={borsaCityPrices} mode={mode} />
        </div>

        <div
          id="hal-list-panel"
          role="tabpanel"
          aria-labelledby="hal-list-tab"
          hidden={view !== "list"}
          className="p-3 sm:p-4"
        >
          {children}
        </div>
      </div>
    </section>
  );
}
